import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReferenceCard from 'components/ReferenceCard';
import Pagination from 'components/Pagination';
import axios from 'axios';
import { parseDoubleEscapedJson } from 'utils/fixDoubleEscapedJSON';

const PER_PAGE = 10;

export default function GlossaryView() {
  const location = useLocation();

  const [results, setResults] = useState({
    pageTitle: '',
    pageItems: [],
  });
  const [current, setCurrent] = useState(1);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async (tid) => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(`/edgconnect/term/refs/${tid}`);
      const data = parseDoubleEscapedJson(res.data);
      setResults(data);
    }
    catch (err) {
      console.error(err);
      setError('Failed to load item details.');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.split('?')[1]);
    const tid = params.get('tid');

    if (!tid) return;

    fetchData(tid);
    setCurrent(1);
  }, [location.hash]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sortedItems = [...(results?.pageItems ?? [])].sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { sensitivity: 'base' })
  );

  const totalPages = Math.ceil(sortedItems.length / PER_PAGE);
  const startIndex = (current - 1) * PER_PAGE + 1;
  const endIndex = Math.min(current * PER_PAGE, sortedItems.length);
  const visibleResults = sortedItems.slice(startIndex - 1, endIndex);

  return (
    <>
    <p className="pb-4" >Please contact the <a href="/data-governance/data-governance-processes">Enterprise Data Governance Team</a> to receive a spreadsheet export of the complete list of reference data.</p>
    <h1 className="mb-3 text-3xl font-semibold text-medium-blue">
      {results.pageTitle?.toUpperCase()}
    </h1>
    <hr></hr>
      <div className="p-4 mb-4 border rounded-lg shadow-md border-black/10 bg-grey-50">
        {loading && <p className="my-3 text-center">
            <svg className="inline-block w-[100px] h-[100px]" alt="loading..." width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" >
              <circle cx="50" cy="50" r="31.8736" fill="none" stroke="#8cd0e5" stroke-width="1">
                <animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate>
                <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate>
              </circle>
              <circle cx="50" cy="50" r="11.6081" fill="none" stroke="#002145" stroke-width="1">
                <animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="0s" repeatCount="indefinite"></animate>
                <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="0s" repeatCount="indefinite"></animate>
              </circle>
            </svg>
          </p>}

        <Pagination
          total={sortedItems.length}
          perPage={PER_PAGE}
          current={current}
          onChange={setCurrent}
        />

        {/* Cards */}
        {visibleResults.map((term) => (
          <ReferenceCard
            key={term.id}
            term={{
              ...term,
              label: term.name,
              description: `${term.description}`,
            }}
            expanded={expandedIds.has(term.id)}
            onToggle={() => toggleExpanded(term.id)}
          />
        ))}

        <Pagination
          total={sortedItems.length}
          perPage={PER_PAGE}
          current={current}
          onChange={setCurrent}
        />

        <div className="my-3 text-sm text-muted">
          {sortedItems.length > 0
            ? `Viewing ${startIndex}-${endIndex} of ${sortedItems.length} results`
            : ''}
        </div>
      </div>
    </>
  );
}
